/* KycSectionFinalOverview.jsx */
import React, { useEffect, useMemo, useState } from "react";
import "../css/KycSectionsStyle.css";
import "../css/KycSectionFinalOverview.css";

import { SelectButton } from "primereact/selectbutton";
import { appConfirm } from "../../../components/ui/appConfirm";
import {
  DEFAULT_RELATIONSHIP_RISK_SETTINGS,
  calculateRelationshipRisk,
  relationshipRiskLabel,
  relationshipRiskTone,
  sanitizeRelationshipRiskSettings,
} from "../../../utils/relationshipRiskRules";

// Firestore client status updates
import { auth, db } from "../../../firebase";
import { doc, updateDoc } from "firebase/firestore";
import { MYKYC_API_BASE } from "../../../config/api";
import {
  clientCarriesPercent,
  linkedEntityHoldsPercent,
  parentCarriesPercent,
} from "../../../utils/editOwnershipPercentage";

const API_BASE = MYKYC_API_BASE;
const PROFILE_SUMMARY_PATH = "/myKyc/analysis/profile-summary";
const SHOW_RELATIONSHIP_MAINTAINED = false;

async function apiFetch(path, { method = "POST", token, body } = {}) {
  const res = await fetch(`${API_BASE}${path}`, {
    method,
    headers: {
      "Content-Type": "application/json",
      ...(token ? { Authorization: `Bearer ${token}` } : {}),
    },
    body: body ? JSON.stringify(body) : undefined,
  });

  const json = await res.json().catch(() => ({}));

  if (!res.ok || json?.ok === false) {
    const err = new Error(json?.error || json?.message || "Request failed");
    err.status = res.status;
    throw err;
  }

  return json;
}

/* ---------------- Helpers ---------------- */

function riskTextFromLevel(level) {
  return relationshipRiskLabel(level);
}

function clean(value) {
  return String(value ?? "").trim();
}

function buildScreeningUsageContext(form, screeningType) {
  return {
    orgId: clean(form?.orgId || form?.customer?.orgId || ""),
    clientId: clean(form?.clientId || form?.id || form?.customer?.clientId || ""),
    screeningType,
    source: "kyc",
  };
}

function riskTone(level) {
  return relationshipRiskTone(level);
}

function industryRiskLevelFromForm(form) {
  const saved =
    form?.internalUse?.industryRiskResult?.final?.riskLevel ??
    form?.internalUse?.industryRiskResult?.auto?.riskLevel ??
    null;

  const n = Number(saved);
  if (n === 3) return "high";
  if (n === 2) return "medium";
  if (n === 1) return "low";
  return null;
}

function geoRiskLevelFromForm(form) {
  const v = String(form?.internalUse?.geoRiskLevel || "").toLowerCase();
  if (v === "high" || v === "medium" || v === "low") return v;
  return null;
}

function pepStatusFromForm(form) {
  const dec = String(form?.pep?.staffDecision || "").toLowerCase();
  if (dec === "flagged_confirmed")
    return { label: "Yes", tone: "high", confirmed: true };
  if (dec === "flagged")
    return { label: "To assess", tone: "medium", confirmed: false };
  if (dec === "no_exposure" || dec === "flagged_remediated" || hasSavedScreeningRun(form?.pep))
    return { label: "No", tone: "low", confirmed: false };
  return { label: "—", tone: "unknown", confirmed: false };
}

function sanctionsStatusFromForm(form) {
  const dec = String(form?.sanctions?.staffDecision || "").toLowerCase();
  if (dec === "flagged_confirmed")
    return { label: "Yes", tone: "high", confirmed: true };
  if (dec === "flagged")
    return { label: "To assess", tone: "medium", confirmed: false };
  if (dec === "no_exposure" || dec === "flagged_remediated" || hasSavedScreeningRun(form?.sanctions))
    return { label: "No", tone: "low", confirmed: false };
  return { label: "—", tone: "unknown", confirmed: false };
}

function hasSavedScreeningRun(screening) {
  if (!screening || typeof screening !== "object") return false;
  return !!(
    screening.result ||
    screening.runAt ||
    screening.completedAt ||
    screening.runCompletedAt ||
    (Array.isArray(screening.rows) && screening.rows.length)
  );
}

function screeningOutcome(
  decision,
  clearLabel,
  confirmedLabel,
  awaitingAssessmentLabel,
  hasRun = false
) {
  const value = clean(decision).toLowerCase();
  if (value === "no_exposure") return clearLabel;
  if (value === "flagged_remediated") return "Reviewed and cleared";
  if (value === "flagged_confirmed") return confirmedLabel;
  if (value === "flagged") return "To assess";
  if (hasRun) return awaitingAssessmentLabel;
  return "Not run";
}

function compactRows(rows, mapper, limit = 8) {
  return (Array.isArray(rows) ? rows : [])
    .slice(0, limit)
    .map(mapper)
    .filter((row) => row && Object.values(row).some((v) => clean(v)));
}

// Ownership rows for the AI summary carry only meaningful %s: each one is the % held in the
// direct parent, named in `ownershipPercentIn`. Below a PIC, passive holding, trust or foundation
// (or for the shareholders of such a client) no % is sent.
function withMeaningfulPercent(summarize, ownership, kind) {
  const nodes = Array.isArray(ownership?.assistantTree?.nodes) ? ownership.assistantTree.nodes : [];
  const byId = new Map(nodes.map((n) => [clean(n?.ID || n?.id), n]));
  return (row) => {
    const out = summarize(row);
    if (!out) return out;
    let parent = null;
    let carries = false;
    if (kind === "shareholder") {
      carries = clientCarriesPercent(ownership);
    } else {
      const node = byId.get(clean(row?.treeNodeId));
      parent = node ? byId.get(clean(node?.parentIDs?.[0])) : null;
      carries = parent ? parentCarriesPercent(parent) : linkedEntityHoldsPercent(row, ownership, null);
    }
    if (!carries || !out.ownershipPercent) return { ...out, ownershipPercent: "" };
    const parentName = kind === "shareholder" ? "the client" : clean(parent?.name) || clean(row?.relatedShareholderName);
    return { ...out, ...(parentName ? { ownershipPercentIn: parentName } : {}) };
  };
}

function entityTypeLabel(value) {
  const raw = clean(value);
  if (!raw) return "";

  const normalized = raw.toLowerCase();
  if (normalized === "pic") return "Domiciliary company (PIC)";
  if (normalized === "nonquotedoperational") return "Operating company";
  if (normalized === "quoted") return "Quoted company";
  if (normalized === "holding") return "Holding company";
  if (normalized === "holdingpassive") return "Passive holding company";
  if (normalized === "holdingactive") return "Active holding company";
  if (normalized === "trust") return "Trust";
  if (normalized === "foundation") return "Foundation";
  if (normalized === "entity") return "Entity";

  return raw;
}

function ownershipRowSummary(row) {
  const type = clean(row?.holderType || row?.type || row?.kind || row?.shareholderType);
  const companyType = clean(row?.shareholderEntityType || row?.companyType);

  return {
    name: clean(row?.name || row?.fullName || row?.shareholderName),
    type: entityTypeLabel(type),
    rawType: type,
    companyType: entityTypeLabel(companyType),
    rawCompanyType: companyType,
    ownershipPercent: clean(row?.ownershipPercent || row?.effectiveOwnershipPercent),
    nationality: clean(row?.nationality),
    domicile: clean(row?.domicile || row?.countryOfResidence || row?.country),
    incorporationCountry: clean(
      row?.incorporationCountry ||
        row?.countryOfIncorporation ||
        row?.countryOfResidence
    ),
    basisOfControl: clean(row?.basisOfControl || row?.controlBasis),
  };
}

function formatAmountRangeWithCurrency(range, currency) {
  const cleanRange = clean(range);
  if (!cleanRange) return "";
  return [cleanRange, clean(currency)].filter(Boolean).join(" ");
}

function isEntityClientFromCustomer(customer) {
  const type = clean(
    customer?.type ||
      customer?.customerType ||
      customer?.clientType ||
      customer?.profileType ||
      customer?.personType ||
      customer?.category
  ).toLowerCase();

  if (type.includes("entity") || type.includes("company") || type.includes("corporate")) {
    return true;
  }
  if (type.includes("individual") || type.includes("person")) return false;
  return customer?.isEntity === true;
}

function sanitizeProfileSummaryForClientType(text, { isEntityClient }) {
  const summary = clean(text);
  if (!summary || isEntityClient) return summary;

  return (summary.match(/[^.!?]+[.!?]?/g) || [summary])
    .filter((sentence) => {
      const lower = sentence.toLowerCase();
      return !(
        lower.includes("ownership structure") ||
        lower.includes("complex structure") ||
        lower.includes("shareholder structure") ||
        lower.includes("ubo structure")
      );
    })
    .map((sentence) => sentence.trim())
    .join(" ")
    .trim();
}

function buildProfileSummaryPayload(form, context) {
  const customer = form?.customer || {};
  const ownership = form?.ownership || {};
  const sow = form?.sourceOfFundsWealth || {};
  const internalUse = form?.internalUse || {};
  const industryAuto = form?.internalUse?.industryRiskResult?.auto || {};
  const isEntityClient = isEntityClientFromCustomer(customer);
  const accountHolderEntityType = clean(
    ownership.desktopClientEntityType || ownership.accountHolderTrustResolution?.kind
  );
  const pepScreeningHasRun = hasSavedScreeningRun(internalUse.pepScreening);
  const sanctionsScreeningHasRun = hasSavedScreeningRun(
    internalUse.sanctionsScreening
  );
  const adverseMediaScreeningHasRun = hasSavedScreeningRun(
    internalUse.adverseMediaScreening
  );

  return {
    customer: {
      name: clean(customer.name),
      type: clean(customer.type),
      clientType: isEntityClient ? "entity" : "individual",
      nationality: clean(customer.nationalityOrIncorporationCountry),
      domicile: clean(customer.country),
      incorporationCountry: clean(customer.nationalityOrIncorporationCountry),
      mainCountryOfOperation: clean(customer.mainCountryOfOperation),
      kycEntityType: entityTypeLabel(accountHolderEntityType),
      rawKycEntityType: accountHolderEntityType,
      businessActivitySummary:
        clean(industryAuto.activitySummary) || clean(customer.businessActivity),
    },
    ownership: isEntityClient
      ? {
          complexStructure: !!ownership.complexStructure,
          directShareholders: compactRows(ownership.shareholders, withMeaningfulPercent(ownershipRowSummary, ownership, "shareholder")),
          ubos: compactRows(ownership.ubos, withMeaningfulPercent(ownershipRowSummary, ownership, "ubo")),
          controllingPersons: compactRows(
            ownership.controllers || ownership.signatories,
            ownershipRowSummary
          ),
          legalArrangements: compactRows(
            ownership.legalArrangements?.arrangements,
            withMeaningfulPercent(ownershipRowSummary, ownership, "arrangement"),
            6
          ),
        }
      : undefined,
    financialProfile: {
      sourceOfWealth: Array.isArray(sow.sourceOfWealth)
        ? sow.sourceOfWealth.map(clean).filter(Boolean)
        : [],
      sourceOfWealthDetails: clean(sow.sourceOfWealthDetails),
      sourceOfFunds: clean(sow.sourceOfFunds),
      sourceOfFundsTypes: Array.isArray(sow.sourceOfFundsTypes)
        ? sow.sourceOfFundsTypes.map(clean).filter(Boolean)
        : [],
      sourceOfFundsDetails: clean(sow.sourceOfFundsDetails),
      sourceOfFundsOtherDetails: clean(sow.sourceOfFundsOtherDetails),
      netWorth: formatAmountRangeWithCurrency(
        sow.netWorthRange,
        sow.netWorthCurrency
      ),
      annualIncome: formatAmountRangeWithCurrency(
        sow.annualIncomeRange,
        sow.annualIncomeCurrency
      ),
    },
    screening: {
      pep: {
        outcome: screeningOutcome(
          form?.pep?.staffDecision,
          "PEP screening cleared",
          "PEP exposure confirmed",
          "PEP screening completed - awaiting compliance assessment",
          pepScreeningHasRun
        ),
        assessmentText: clean(form?.pep?.staffAssessment),
      },
      sanctions: {
        outcome: screeningOutcome(
          form?.sanctions?.staffDecision,
          "Sanctions screening cleared",
          "Sanctions exposure confirmed",
          "Sanctions screening completed - awaiting compliance assessment",
          sanctionsScreeningHasRun
        ),
        assessmentText: clean(form?.sanctions?.staffAssessment),
      },
      adverseMedia: {
        outcome:
          form?.adverseMedia?.negativeNews === false
            ? "Adverse media screening cleared"
            : form?.adverseMedia?.negativeNews === true &&
              form?.adverseMedia?.status === "confirmed"
            ? "Negative news confirmed"
            : form?.adverseMedia?.negativeNews === true &&
              form?.adverseMedia?.status === "remediated"
            ? "Reviewed and cleared"
            : adverseMediaScreeningHasRun
            ? "Adverse media screening completed - awaiting compliance assessment"
            : "Not run",
        assessmentText: clean(form?.adverseMedia?.comment),
      },
    },
    riskContext: {
      geoRisk: riskTextFromLevel(context.geoRisk),
      industryRisk: riskTextFromLevel(context.industryRisk),
      notMetInPerson: !!context.notMetInPerson,
      complexStructure: isEntityClient ? !!context.complexStructure : undefined,
    },
  };
}

/* ---------------- Component ---------------- */

export default function KycSectionFinalOverview({
  sectionNo = 0,
  form,
  onSetAt,
  readOnly = false,
  clientId = "", // needed to update Firestore status
  relationshipRiskSettings = DEFAULT_RELATIONSHIP_RISK_SETTINGS,
}) {
  const profileSummaryText = String(
    form?.finalOverview?.profileSummary || ""
  ).trim();
  const amlAssessmentText = String(form?.finalOverview?.assessment || "").trim();
  const complianceObservationText = String(
    form?.finalOverview?.complianceObservation || ""
  ).trim();
  const [generatingProfileSummary, setGeneratingProfileSummary] =
    useState(false);
  const [profileSummaryError, setProfileSummaryError] = useState("");
  const notMetInPerson = !!form?.engagement?.noMeetingWithContractingPartner;

  const geoRisk = geoRiskLevelFromForm(form);
  const industryRisk = industryRiskLevelFromForm(form);

  const complexStructure = !!form?.ownership?.complexStructure;
  // tri-state: true | false | null/undefined
  const negativeNews = form?.adverseMedia?.negativeNews;

  const pepStatus = pepStatusFromForm(form);
  const sanctionsStatus = sanctionsStatusFromForm(form);

  const pepExposure = pepStatus.confirmed;
  const sanctionsExposure = sanctionsStatus.confirmed;

  const riskCalc = useMemo(() => {
    return calculateRelationshipRisk(
      {
        industryRisk,
        geoRisk,
        pepExposure,
        sanctionsExposure,
        complexStructure,
        notMetInPerson,
        negativeNews,
      },
      sanitizeRelationshipRiskSettings(relationshipRiskSettings)
    );
  }, [
    industryRisk,
    geoRisk,
    pepExposure,
    sanctionsExposure,
    complexStructure,
    notMetInPerson,
    negativeNews,
    relationshipRiskSettings,
  ]);

  // Persist calculated risk
  useEffect(() => {
    if (typeof onSetAt !== "function") return;
    const current = String(form?.finalOverview?.calculatedRiskRating || "");
    const next = riskCalc.label;
    if (current !== next) {
      onSetAt(["finalOverview", "calculatedRiskRating"], next);
    }
    const currentResult = JSON.stringify(form?.finalOverview?.relationshipRiskResult || {});
    const nextResult = JSON.stringify(riskCalc);
    if (currentResult !== nextResult) {
      onSetAt(["finalOverview", "relationshipRiskResult"], riskCalc);
    }
  }, [riskCalc, form?.finalOverview?.calculatedRiskRating, form?.finalOverview?.relationshipRiskResult, onSetAt]);

  const tone = riskTone(riskCalc.level);

  const overallRiskReady = useMemo(() => {
    const geoRiskComplete = geoRisk != null;
    const industryRiskComplete = industryRisk != null;
    const negativeNewsComplete = negativeNews === true || negativeNews === false;
    const sanctionsComplete = sanctionsStatus.tone !== "unknown";
    const pepComplete = pepStatus.tone !== "unknown";
    return geoRiskComplete && industryRiskComplete && negativeNewsComplete && sanctionsComplete && pepComplete;
  }, [geoRisk, industryRisk, negativeNews, sanctionsStatus.tone, pepStatus.tone]);

  // ---- options ----
  const complianceApprovalOptions = useMemo(
    () => [
      { label: "Approved", value: "yes" },
      { label: "Not Approved", value: "no" },
    ],
    []
  );

  const onboardedOptions = useMemo(
    () => [
      { label: "Onboarded", value: true },
      { label: "Not Onboarded", value: false },
    ],
    []
  );

  const relMaintainedOptions = useMemo(
    () => [
      { label: "Maintained", value: true },
      { label: "Not Maintained", value: false },
    ],
    []
  );

  // ---- Values ----
  const relationshipApproved = String(
    form?.finalOverview?.relationshipAccepted || ""
  ); // "" | "yes" | "no"
  const clientOnboarded = form?.finalOverview?.clientOnboarded; // true | false | null/undefined
  const relationshipMaintained = form?.finalOverview?.relationshipMaintained; // true | false | null/undefined

  // ✅ gating selection checks
  const relApprovedSelected =
    relationshipApproved === "yes" || relationshipApproved === "no";
  const onboardedSelected =
    clientOnboarded === true || clientOnboarded === false;

  // ✅ always render toggles, but disable/grey based on gating
  const onboardedUnavailable = !relApprovedSelected;
  // third enabled only if (1) first selected AND (2) second selected AND (3) onboarded === true
  const maintainedUnavailable =
    !relApprovedSelected || !onboardedSelected || clientOnboarded !== true;

  // -------------------------------------------
  // Preselect onboarding from Firestore client status
  // -------------------------------------------
  // const statusRaw =
  //   form?.customer?.status ?? form?.client?.status ?? form?.clientStatus ?? "";

  // -------------------------------------------
  // Client status (from clients/{id} document)
  // Stored in the KYC form as: form.clientStatus
  // -------------------------------------------
  const status = String(form?.clientStatus || "")
    .trim()
    .toLowerCase(); // prospect | client | former

  // const statusRaw =
  //   form?.customer?.clientStatus ??
  //   form?.customer?.status ??
  //   form?.client?.status ??
  //   form?.clientStatus ??
  //   "";
  // const status = String(statusRaw || "").toLowerCase();

  // useEffect(() => {
  //   if (typeof onSetAt !== "function") return;

  //   const curOnb = form?.finalOverview?.clientOnboarded;
  //   const onbIsSet = curOnb === true || curOnb === false;

  //   // Only auto-set if user hasn't set it yet
  //   if (onbIsSet) return;

  //   if (status === "prospect") {
  //     onSetAt(["finalOverview", "clientOnboarded"], false);
  //     return;
  //   }

  //   if (status === "client" || status === "former") {
  //     onSetAt(["finalOverview", "clientOnboarded"], true);
  //   }
  // }, [status, form?.finalOverview?.clientOnboarded, onSetAt]);

  useEffect(() => {
    if (typeof onSetAt !== "function") return;

    if (!relApprovedSelected) {
      if (form?.finalOverview?.clientOnboarded != null) {
        onSetAt(["finalOverview", "clientOnboarded"], null);
      }
      if (form?.finalOverview?.relationshipMaintained != null) {
        onSetAt(["finalOverview", "relationshipMaintained"], null);
      }
    }
  }, [
    relApprovedSelected,
    form?.finalOverview?.clientOnboarded,
    form?.finalOverview?.relationshipMaintained,
    onSetAt,
  ]);

  // -------------------------------------------
  // Firestore: update status on toggle clicks
  // -------------------------------------------
  async function setClientStatus(nextStatus) {
    if (!clientId || readOnly) return;
    try {
      await updateDoc(doc(db, "clients", clientId), {
        status: nextStatus, // prospect | client | former
      });
    } catch (e) {}
  }

  // -------------------------------------------
  // Title click: clear selection (as you requested)
  // -------------------------------------------
  function clearRelationshipApproved() {
    onSetAt?.(["finalOverview", "relationshipAccepted"], "");
    onSetAt?.(["finalOverview", "clientOnboarded"], null);
    onSetAt?.(["finalOverview", "relationshipMaintained"], null);
  }

  function clearClientOnboarded() {
    onSetAt?.(["finalOverview", "clientOnboarded"], null);
    onSetAt?.(["finalOverview", "relationshipMaintained"], null);
  }

  function clearRelationshipMaintained() {
    onSetAt?.(["finalOverview", "relationshipMaintained"], null);
  }

  // -------------------------------------------
  // Toggle change handlers
  // -------------------------------------------
  function onChangeRelationshipApproved(e) {
    const v = e.value ?? "";
    onSetAt?.(["finalOverview", "relationshipAccepted"], v);

    // if cleared => clear downstream
    if (!v) {
      onSetAt?.(["finalOverview", "clientOnboarded"], null);
      onSetAt?.(["finalOverview", "relationshipMaintained"], null);
    }
  }

  function onChangeClientOnboarded(e) {
    const v = e.value; // true/false
    onSetAt?.(["finalOverview", "clientOnboarded"], v);

    // not onboarded => clear maintained
    if (v === false)
      onSetAt?.(["finalOverview", "relationshipMaintained"], null);

    // status rules
    if (v === true) setClientStatus("client");
    if (v === false) setClientStatus("prospect");
  }

  function onChangeRelationshipMaintained(e) {
    const v = e.value; // true/false
    onSetAt?.(["finalOverview", "relationshipMaintained"], v);

    // status rules
    if (v === true) setClientStatus("client");
    if (v === false) setClientStatus("former");
  }

  async function runProfileSummaryGeneration() {
    setProfileSummaryError("");
    setGeneratingProfileSummary(true);
    try {
      const token = auth.currentUser ? await auth.currentUser.getIdToken() : "";
      const profile = buildProfileSummaryPayload(form, {
        geoRisk,
        industryRisk,
        notMetInPerson,
        complexStructure,
      });
      const json = await apiFetch(PROFILE_SUMMARY_PATH, {
        token,
        body: {
          profile,
          screeningUsage: buildScreeningUsageContext(form, "profile_summary"),
        },
      });
      const isEntityClient = isEntityClientFromCustomer(form?.customer || {});
      const draft = sanitizeProfileSummaryForClientType(
        json?.profileSummary || json?.data?.profileSummary,
        { isEntityClient }
      );
      if (!draft) throw new Error("Empty generated profile summary.");
      onSetAt?.(["finalOverview", "profileSummary"], draft);
    } catch (e) {
      setProfileSummaryError(e?.message || "Failed to generate profile summary.");
    } finally {
      setGeneratingProfileSummary(false);
    }
  }

  function generateProfileSummaryDraft() {
    if (readOnly || generatingProfileSummary) return;

    if (!profileSummaryText) {
      runProfileSummaryGeneration();
      return;
    }

    appConfirm({
      group: "appConfirm",
      header: "Replace profile summary",
      message: "Replace the existing profile summary with a generated draft?",
      defaultFocus: "reject",
      acceptLabel: "Replace",
      rejectLabel: "Cancel",
      scheme: "danger",
      icon: "bi bi-exclamation-triangle",
      accept: runProfileSummaryGeneration,
    });
  }

  return (
    <div className="card kycCard mb-3">
      <div className="kycSectionHead">
        <h5 className="kycSectionTitle">{sectionNo} | Final overview</h5>
      </div>

      <div className="kycSectionBody">
        {/* ---------------- HERO ---------------- */}
        <div
          className={overallRiskReady ? `kycFinalHero kycFinalHero--${tone}` : "kycFinalHero"}
          style={!overallRiskReady ? { background: "#DEE0E6" } : undefined}
        >
          <div className="kycFinalHeroLeft">
            <div className="kycFinalHeroLabel">Relationship risk rating</div>
            <div className="kycFinalHeroRisk">
              {overallRiskReady ? riskTextFromLevel(riskCalc.level) : "To be determined"}
            </div>
            <div className="kycFinalHeroSub">
              {overallRiskReady ? (
                riskCalc.reasons?.length ? (
                  <ul className="kycFinalHeroList">
                    {riskCalc.reasons.map((r) => (
                      <li key={r}>{r}</li>
                    ))}
                  </ul>
                ) : (
                  <div className="text-muted small">—</div>
                )
              ) : (
                <div className="small" style={{ color: "#8A8FA8", maxWidth: "66.66%" }}>
                  Complete Geo risk, Sanctions, PEP, Industry risk, Negative news and Complex structure to determine the overall risk.
                </div>
              )}
            </div>
          </div>

          <div className="kycFinalHeroRight">
            <div className={`kycFinalBadge kycFinalBadge--${overallRiskReady ? tone : "unknown"}`}>
              {!overallRiskReady ? (
                <>
                  <i className="bi bi-hourglass-split me-2" />
                  TBD
                </>
              ) : tone === "high" ? (
                <>
                  <i className="bi bi-exclamation-triangle-fill me-2" />
                  High risk
                </>
              ) : tone === "medium" ? (
                <>
                  <i className="bi bi-exclamation-circle-fill me-2" />
                  Medium risk
                </>
              ) : tone === "low" ? (
                <>
                  <i className="bi bi-check-circle-fill me-2" />
                  Standard risk
                </>
              ) : (
                <>
                  <i className="bi bi-question-circle-fill me-2" />
                  Not assessed
                </>
              )}
            </div>

            <div className="kycFinalMiniGrid">
              <div className="kycFinalMiniItem">
                <div className="kycFinalMiniK">Geo risk</div>
                <div className={`kycFinalMiniV kycFinalMiniV--${riskTone(geoRisk)}`}>
                  {riskTextFromLevel(geoRisk)}
                </div>
              </div>

              <div className="kycFinalMiniItem">
                <div className="kycFinalMiniK">Industry risk</div>
                <div className={`kycFinalMiniV kycFinalMiniV--${riskTone(industryRisk)}`}>
                  {riskTextFromLevel(industryRisk)}
                </div>
              </div>

              <div className="kycFinalMiniItem">
                <div className="kycFinalMiniK">Complex structure</div>
                <div className={`kycFinalMiniV kycFinalMiniV--${complexStructure ? "medium" : "low"}`}>
                  {complexStructure ? "Yes" : "No"}
                </div>
              </div>

              <div className="kycFinalMiniItem">
                <div className="kycFinalMiniK">PEP</div>
                <div className={`kycFinalMiniV kycFinalMiniV--${pepStatus.tone}`}>
                  {pepStatus.label}
                </div>
              </div>

              <div className="kycFinalMiniItem">
                <div className="kycFinalMiniK">Sanctions</div>
                <div className={`kycFinalMiniV kycFinalMiniV--${sanctionsStatus.tone}`}>
                  {sanctionsStatus.label}
                </div>
              </div>

              <div className="kycFinalMiniItem">
                <div className="kycFinalMiniK">Negative news</div>
                <div
                  className={`kycFinalMiniV kycFinalMiniV--${
                    negativeNews === true ? "high" : negativeNews === false ? "low" : "unknown"
                  }`}
                >
                  {negativeNews === true ? "Yes" : negativeNews === false ? "No" : "—"}
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* ---------------- OUTCOME & DECISION ---------------- */}
        <div className="mt-3 mb-2 fw-semibold" style={{ color: "#4C5465" }}>
          Outcome & decision
        </div>

        <div className="row g-3 align-items-stretch">
          <div className="col-12 col-md-4">
            <div
              className={`kycFinalDecisionCard h-100 ${
                SHOW_RELATIONSHIP_MAINTAINED
                  ? ""
                  : "kycFinalDecisionCard--twoItems"
              }`}
            >
              {/* 1) Relationship approved */}
              <div className="kycFinalDecisionStackItem">
                <button
                  type="button"
                  className="kycFinalDecisionTitleBtn"
                  onClick={readOnly ? undefined : clearRelationshipApproved}
                >
                  Relationship approved?
                </button>

                <div
                  className="kycDecisionCenter"
                  style={readOnly ? { pointerEvents: "none" } : undefined}
                >
                  <div
                    className={`kycSelectDecisions ${
                      relationshipApproved === "yes"
                        ? "is-yes"
                        : relationshipApproved === "no"
                        ? "is-no"
                        : ""
                    }`}
                  >
                    <SelectButton
                      value={relationshipApproved}
                      options={complianceApprovalOptions}
                      onChange={onChangeRelationshipApproved}
                      disabled={false}
                    />
                  </div>
                </div>
              </div>

              {/* 2) Client onboarded (ALWAYS rendered, but gated) */}
              <div
                className={`kycFinalDecisionStackItem ${
                  onboardedUnavailable ? "is-disabled-strong" : ""
                }`}
              >
                <button
                  type="button"
                  className="kycFinalDecisionTitleBtn"
                  onClick={readOnly ? undefined : clearClientOnboarded}
                >
                  Client onboarded?
                </button>

                <div
                  className="kycDecisionCenter"
                  style={readOnly ? { pointerEvents: "none" } : undefined}
                >
                  <div
                    className={`kycSelectDecisions ${
                      clientOnboarded === true
                        ? "is-yes"
                        : clientOnboarded === false
                        ? "is-no"
                        : ""
                    }`}
                  >
                    <SelectButton
                      value={clientOnboarded ?? null}
                      options={onboardedOptions}
                      onChange={onChangeClientOnboarded}
                      disabled={!readOnly && onboardedUnavailable}
                    />
                  </div>
                </div>
              </div>

              {SHOW_RELATIONSHIP_MAINTAINED ? (
                <>
                  {/* 3) Relationship maintained (ALWAYS rendered, but gated) */}
                  <div
                    className={`kycFinalDecisionStackItem ${
                      maintainedUnavailable ? "is-disabled-strong" : ""
                    }`}
                  >
                    <button
                      type="button"
                      className="kycFinalDecisionTitleBtn"
                      onClick={
                        readOnly ? undefined : clearRelationshipMaintained
                      }
                    >
                      Relationship maintained?
                    </button>

                    <div
                      className="kycDecisionCenter"
                      style={readOnly ? { pointerEvents: "none" } : undefined}
                    >
                      <div
                        className={`kycSelectDecisions ${
                          relationshipMaintained === true
                            ? "is-yes"
                            : relationshipMaintained === false
                            ? "is-no"
                            : ""
                        }`}
                      >
                        <SelectButton
                          value={relationshipMaintained ?? null}
                          options={relMaintainedOptions}
                          onChange={onChangeRelationshipMaintained}
                          disabled={!readOnly && maintainedUnavailable}
                        />
                      </div>
                    </div>
                  </div>
                </>
              ) : null}
            </div>
          </div>

          {/* Profile summary */}
          <div className="col-12 col-md-8">
            <div className="kycFinalProfileBox h-100">
              <div className="kycFinalProfileHead">
                <div className="text-muted small">Profile summary</div>
                {!readOnly ? (
                  <button
                    type="button"
                    className="kycFinalGenerateProfileBtn"
                    onClick={generateProfileSummaryDraft}
                    disabled={generatingProfileSummary}
                  >
                    {generatingProfileSummary ? (
                      <>
                        <span
                          className="spinner-border spinner-border-sm"
                          aria-hidden="true"
                        />
                        Generating
                      </>
                    ) : profileSummaryText ? (
                      "Regenerate draft"
                    ) : (
                      "Generate draft"
                    )}
                  </button>
                ) : null}
              </div>
              {readOnly ? (
                <div
                  className="form-control"
                  style={{
                    minHeight: 260,
                    whiteSpace: "pre-wrap",
                    textAlign: "left",
                  }}
                >
                  {profileSummaryText || "Profile summary not provided"}
                </div>
              ) : (
                <textarea
                  className="form-control"
                  rows={11}
                  value={form?.finalOverview?.profileSummary || ""}
                  onChange={(e) =>
                    onSetAt?.(["finalOverview", "profileSummary"], e.target.value)
                  }
                  disabled={readOnly}
                />
              )}
              {profileSummaryError ? (
                <div className="kycFinalProfileError">
                  {profileSummaryError}
                </div>
              ) : null}
            </div>
          </div>
        </div>

        {/* AML final assessment */}
        <div className="mt-4">
          <div className="text-muted small mb-1">AML final assessment</div>
          {readOnly ? (
            <div
              className="form-control"
              style={{
                minHeight: amlAssessmentText ? 144 : 84,
                whiteSpace: "pre-wrap",
                textAlign: "left",
              }}
            >
              {amlAssessmentText || "No AML assessment provided"}
            </div>
          ) : (
            <textarea
              className="form-control"
              rows={6}
              value={form?.finalOverview?.assessment || ""}
              onChange={(e) =>
                onSetAt?.(["finalOverview", "assessment"], e.target.value)
              }
              disabled={readOnly}
            />
          )}
        </div>

        {/* Compliance observation */}
        {!readOnly || complianceObservationText ? (
          <div className="mt-3">
            <div className="text-muted small mb-1">
              Compliance observation (optional)
            </div>
            {readOnly ? (
              <div
                className="form-control"
                style={{ minHeight: 108, whiteSpace: "pre-wrap" }}
              >
                {form?.finalOverview?.complianceObservation || ""}
              </div>
            ) : (
              <textarea
                className="form-control"
                rows={4}
                value={form?.finalOverview?.complianceObservation || ""}
                onChange={(e) =>
                  onSetAt?.(
                    ["finalOverview", "complianceObservation"],
                    e.target.value
                  )
                }
                disabled={readOnly}
              />
            )}
          </div>
        ) : null}
      </div>
    </div>
  );
}
